import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  InputAdornment,
  Grid,
  Stack,
  Divider
} from "@mui/material";
import { sendOtpThunk } from "../redux/thunk/loginThunk";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "@store/store";
import OtpForm from "./otpForm";
import { LoginSchema } from "../formikSchema";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { aadharRegisterThunk } from "@modules/register/redux/thunk/registerThunk";
import { resetUserRegistration } from "@modules/register/redux/registerSlice";

const LoginForm = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [otpSent, setOtpSent] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [error, setError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: { mobile: mobileNumber || "" },
    validationSchema: LoginSchema,
    onSubmit: async values => {
      try {
        const otpResponse = await dispatch(
          sendOtpThunk({ mobile_number: "+91" + values.mobile })
        );
        if (otpResponse.meta.requestStatus === "fulfilled") {
          setOtpSent(true);
          setMobileNumber(values.mobile);
          toast.success(otpResponse?.payload?.message);
        } else {
          toast.error(otpResponse?.payload?.message || "Failed to send OTP");
          console.error("Failed to send OTP");
        }
      } catch (error) {
        console.error("Error during form submission:", error);
      }
    }
  });

  const handleBack = () => {
    setOtpSent(false);
    setMobileNumber("");
    formik.resetForm();
  };

  const handleResendOtp = async () => {
    try {
      const otpResponse = await dispatch(
        sendOtpThunk({ mobile_number: "+91" + mobileNumber })
      );
      if (otpResponse.meta.requestStatus === "fulfilled") {
        toast.success(
          otpResponse?.payload?.message || "OTP resent successfully"
        );
        return true;
      } else {
        toast.error(otpResponse?.payload?.message || "Failed to resend OTP");
        return false;
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      toast.error("Failed to resend OTP");
      return false;
    }
  };

  useEffect(() => {
    dispatch(resetUserRegistration());
  }, []);

  return (
    <Box>
      {!otpSent ? (
        <form
          onSubmit={formik.handleSubmit}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <Paper
            elevation={8}
            sx={{
              width: { xs: "100%" },
              maxWidth: "100%",
              mx: "auto",
              my: { xs: 0 },
              borderRadius: 1,
              p: { xs: 2, sm: 3, md: 3 },
              bgcolor: "#fff",
              height: "fit-content"
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              mb={0.5}
              spacing={0.5}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 20, sm: 22, md: 24 },
                  lineHeight: 1.1,
                  color: "#212121",
                  mb: { xs: 0.5, sm: 1 }
                }}
              >
                Applicant Login
              </Typography>

              <Button
                component="a"
                size="small"
                href={
                  process.env.REACT_APP_ENV == "prod"
                    ? `${process.env.REACT_APP_API_BASE_URL}/authority`
                    : `${process.env.REACT_APP_API_BASE_AUTHORITY_URL}/authority`
                }
                startIcon={<LockOutlinedIcon />}
                sx={{
                  color: "#3A1C73",
                  fontSize: { xs: 13, sm: 14 },
                  fontWeight: 700,
                  textTransform: "none",
                  textDecoration: "underline",
                  minWidth: 0,
                  p: 0,
                  "&:hover": {
                    bgcolor: "transparent",
                    textDecoration: "underline"
                  }
                }}
              >
                Authority Login
              </Button>
            </Stack>

            <Typography
              sx={{
                color: "#212121",
                display: "block",
                mt: 1,
                mb: 0.5,
                fontWeight: 400,
                fontSize: { xs: 13, sm: 14 }
              }}
            >
              Enter your Registered Mobile Number
            </Typography>

            <TextField
              fullWidth
              id="aadhaar"
              name="mobile"
              placeholder="Enter your Mobile Number"
              className={`aadhaar-input ${formik.touched.mobile && formik.errors.mobile
                  ? "validation-input"
                  : ""
                }`}
              value={formik.values.mobile}
              onChange={e => {
                const value = e.target.value;
                if (/^\d{0,10}$/.test(value)) {
                  formik.setFieldValue("mobile", value);
                }
              }}
              error={formik.touched.mobile && Boolean(formik.errors.mobile)}
              helperText={formik.touched.mobile && formik.errors.mobile}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 44,
                  marginBottom: "0.4rem",
                  borderRadius: 2,
                  bgcolor: "#fff",
                  "& fieldset": { borderColor: "# " },
                  "&:hover fieldset": { borderColor: "#D1D5DB" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#CFCFCF",
                    borderWidth: 1
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">+91</InputAdornment>
                )
              }}
            />

            <Button
              type="submit"
              className="btnContinue"
              fullWidth
              sx={{ fontSize: { xs: 13, sm: 14 }, height: 40 }}
            >
              Login With OTP
            </Button>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ my: 1 }}
            >
              <Divider sx={{ flex: "0 0 20px", borderColor: "#E5E7EB" }} />
              <Typography
                sx={{
                  color: "#9CA3AF",
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  fontSize: { xs: 11, sm: 12 }
                }}
              >
                OR
              </Typography>
              <Divider sx={{ flex: "0 0 20px", borderColor: "#E5E7EB" }} />
            </Stack>
                        <Button
                fullWidth
                variant="outlined"
                onClick={async e => {
                  e.preventDefault();
                  const res = await dispatch(aadharRegisterThunk()).unwrap();
                  if (res.redirectURL) {
                    window.location.href = res.redirectURL;
                  }
                }}
                sx={{
                  color: "#3A1C73",
                  borderColor: "#3A1C73",
                  borderWidth: 1,
                  "&:hover": {
                    borderColor: "#3A1C73",
                    bgcolor: "rgba(59,26,119,0.04)"
                  },
                  fontWeight: 500,
                  textTransform: "none",
                  height: 40,
                  borderRadius: 999,
                  fontSize: 14,
                  mb: 2
                }}
              >
                Register as an Applicant
              </Button>

            {/* <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate("/organisation")}
              sx={{
                color: "#3A1C73",
                borderColor: "#3A1C73",
                borderWidth: 1,
                "&:hover": {
                  borderColor: "#3A1C73",
                  bgcolor: "rgba(59,26,119,0.04)"
                },
                fontWeight: 500,
                textTransform: "none",
                height: 40,
                borderRadius: 999,
                fontSize: 14,
                mb: 2
              }}
            >
              Register as Organisation Admin
            </Button> */}

            <Typography
              sx={{
                fontSize: { xs: 9, sm: 10 },
                color: "#61646b",
                textAlign: "center"
              }}
            >
              You will redirect to the <b>Meri Pehchaan</b> website for
              Aadhaar Registration
            </Typography>
          </Paper>
        </form>
      ) : (
        <Box sx={{ width: { xs: "100%", sm: 400, md: 400 }, maxWidth: "100%", mx: "auto" }}>
          <OtpForm
            handleBack={handleBack}
            mobile={mobileNumber}
            onResendOtp={handleResendOtp}
          />
        </Box>
      )}
    </Box>
  );
};

export default LoginForm;
